import { useSearchParams } from 'react-router-dom';
import { PageHeader } from '../ui/PageHeader';
import { TabPanel, Tabs } from '../ui/Tabs';
import { PeriodBar } from '../sales/PeriodBar';
import { usePeriodParams } from '../sales/usePeriodParams';
import { useCan } from '../useCan';
import { API_BASE } from '../../core/api/http';
import type { Permission } from '../../core/types/team';
import { OverviewTab } from './tabs/OverviewTab';
import { CustomersTab } from './tabs/CustomersTab';
import { SalonTab } from './tabs/SalonTab';
import { WebshopTab } from './tabs/WebshopTab';
import { SourcesTab } from './tabs/SourcesTab';
import { ReachTab } from './reach/ReachTab';

// Klanten (met namen) en de omzet per behandeling of product: alleen met die bevoegdheid. Marketing
// ziet overzicht, bereik en bronnen (zelfde regel als de server, core/rules/endpointPermissions.ts).
const allTabs = [{
  key: 'overzicht',
  label: 'Overzicht'
}, {
  key: 'klanten',
  label: 'Klanten',
  permission: 'customers.view'
}, {
  key: 'salon',
  label: 'Salon',
  permission: 'revenue.view'
}, {
  key: 'webshop',
  label: 'Webshop',
  permission: 'revenue.view'
}, {
  key: 'bereik',
  label: 'Bereik'
}, {
  key: 'bronnen',
  label: 'Bronnen'
}] as const satisfies readonly {
  key: string;
  label: string;
  permission?: Permission;
}[];
type TabKey = (typeof allTabs)[number]['key'];

/**
 * Verkoop › Inzicht (docs/INZICHT.md): wat werkt, zonder cookies en zonder personen te volgen.
 * Tabblad en periode staan in het adres; de periode is dezelfde als bij Omzet.
 */
export function Insights() {
  const {
    canAll
  } = useCan();
  const tabs = allTabs.filter(t => !('permission' in t) || canAll(t.permission));
  const [params, setParams] = useSearchParams();
  const tab: TabKey = tabs.find(t => t.key === params.get('tab'))?.key ?? 'overzicht';
  const period = usePeriodParams();
  const setTab = (next: TabKey) => setParams(prev => {
    const p = new URLSearchParams(prev);
    if (next === 'overzicht') p.delete('tab');else p.set('tab', next);
    p.delete('bereik');
    return p;
  }, {
    replace: true
  });
  return <div data-id="element-1813" data-mp="10aaixicn43mw">
      <PageHeader title="Inzicht" description={`Wat werkt, en wat je anders kunt doen. Zonder cookies; we tellen bezoek, we volgen niemand.${API_BASE === null ? ' Voorbeeldcijfers uit de demo.' : ''}`} data-id="element-1814" data-mp="10aivta8rj8y3" />
      <PeriodBar state={period} data-id="element-1815" data-mp="1a1wz7t3u4swi" />
      <Tabs id="inzicht" label="Inzicht" tabs={tabs} value={tab} onChange={setTab} className="mb-6" data-id="element-1816" data-mp="mbasq69hbgom" />
      <TabPanel id="inzicht" value={tab} data-id="element-1817" data-mp="hcpta921x118">
        {tab === 'overzicht' && <OverviewTab period={period} data-id="element-1818" data-mp="hekhbw8md99j" />}
        {tab === 'klanten' && <CustomersTab period={period} data-id="element-1819" data-mp="vdaflf76dezh" />}
        {tab === 'salon' && <SalonTab period={period} data-id="element-1820" data-mp="ravtmm87lz3z" />}
        {tab === 'webshop' && <WebshopTab period={period} data-id="element-1821" data-mp="r9cckteqbk15" />}
        {tab === 'bereik' && <ReachTab period={period} data-id="element-1822" data-mp="26qmkzuil25nr" />}
        {tab === 'bronnen' && <SourcesTab period={period} data-id="element-1823" data-mp="26t8rrrxngh4x" />}
      </TabPanel>
    </div>;
}